// File: src/components/host/HostEventGalleryEditable.jsx

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { LuImage } from 'react-icons/lu';
import axios from 'axios';

export default function HostEventGalleryEditable({
  images = [],               // [url, url, url]
  maxImages = 3,
  onAddImages,               // (nextImages[]) => void
  readOnly = false,

  // ✅ NEW: tell gallery it’s being used inside the desktop modal
  // (it will still stay "mobile" when viewport is small)
  inModal = false,
}) {
  const scrollerRef = useRef(null);
  const fileInputRef = useRef(null);
  const idleTimerRef = useRef(null);

  const [focusIndex, setFocusIndex] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);

  // ✅ responsive breakpoint (desktop-ish)
  const [isDesktop, setIsDesktop] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(min-width: 900px)').matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mq = window.matchMedia('(min-width: 900px)');
    const onChange = () => setIsDesktop(mq.matches);

    // Safari compat
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);

    setIsDesktop(mq.matches);

    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  const useGrid = Boolean(inModal && isDesktop);

  const slots = useMemo(
    () => Array.from({ length: maxImages }).map((_, i) => images[i] || null),
    [images, maxImages]
  );

  // keep focusIndex in range
  useEffect(() => {
    if (focusIndex > slots.length - 1) setFocusIndex(Math.max(0, slots.length - 1));
  }, [slots.length, focusIndex]);

  // ✅ Center-focus logic (ONLY for mobile carousel mode)
  useEffect(() => {
    if (useGrid) return;

    const scroller = scrollerRef.current;
    if (!scroller) return;

    const calc = () => {
      const rect = scroller.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;

      let nearestIdx = 0;
      let nearestDist = Infinity;

      const { children } = scroller;
      for (let i = 0; i < children.length; i++) {
        const el = children[i];
        const r = el.getBoundingClientRect();
        const cardCenterX = r.left + r.width / 2;
        const dist = Math.abs(cardCenterX - centerX);

        if (dist < nearestDist) {
          nearestDist = dist;
          nearestIdx = i;
        }
      }

      setFocusIndex(nearestIdx);
    };

    const onScroll = () => {
      setIsScrolling(true);
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => setIsScrolling(false), 160);
      calc();
    };

    scroller.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', calc);
    calc();

    return () => {
      scroller.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', calc);
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [slots.length, useGrid]);

  // 🔑 Upload image into focused slot
  async function handlePickFiles(e) {
    if (readOnly) return;

    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const formData = new FormData();
      formData.append('image', file);

      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/events/gallery-image`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      const imageUrl = res.data?.url;
      if (!imageUrl) throw new Error('No image URL returned');

      const next = [...slots];
      next[focusIndex] = imageUrl;

      if (typeof onAddImages === 'function') onAddImages(next);
    } catch (err) {
      console.error('[event gallery upload] →', err);
      alert('Failed to upload image');
    } finally {
      e.target.value = null;
    }
  }

  const listStyle = useGrid ? S.grid : S.scroller;

  return (
    <div style={S.wrap}>
      <div ref={scrollerRef} style={listStyle} className="hide-scrollbar">
        {slots.map((img, i) => {
          const isFocused = i === focusIndex;

          const cardStyle = {
            ...S.card,
            ...(useGrid ? S.cardGrid : S.cardCarousel),
            border: img ? '1px solid #E5E7EB' : '1.5px dashed #D1D5DB',
            cursor: readOnly ? 'default' : 'pointer',
            ...(isFocused && !readOnly
              ? {
                  border: '1px solid #ddd',
                }
              : {}),
          };

          return (
            <div
              key={i}
              style={cardStyle}
              onClick={() => {
                if (readOnly) return;
                setFocusIndex(i);
                fileInputRef.current?.click();
              }}
            >
              {img ? (
                <img
                  src={img}
                  alt={`event-img-${i}`}
                  style={{
                    ...S.img,
                    opacity: readOnly ? 0.65 : 1,
                  }}
                />
              ) : (
                <div style={S.empty}>
                  <LuImage size={32} color="#9CA3AF" strokeWidth={1.5} />
                </div>
              )}

              {/* Upload badge — focused slot only, edit mode only */}
              {!readOnly && isFocused && (
                <div style={{ ...S.uploadBadge, ...(useGrid ? S.uploadBadgeGrid : null) }}>
                  <span style={S.plusBig}>+</span>
                  <span>Upload image</span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Dots (mobile carousel only) */}
      {!useGrid && maxImages > 1 && (
        <div style={S.dots}>
          {slots.map((_, idx) => (
            <div
              key={idx}
              style={{
                ...S.dot,
                ...(idx === focusIndex ? S.dotActive : {}),
                ...(isScrolling ? { opacity: 0.9 } : null),
              }}
            />
          ))}
        </div>
      )}

      {!readOnly && (
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handlePickFiles}
        />
      )}
    </div>
  );
}

const S = {
  wrap: {
    margin: '0 0px 12px',
    borderRadius: 12,
    background: '#fff',
    padding: 0,
  },

  // ✅ Mobile / default: horizontal scroll
  scroller: {
    display: 'flex',
    overflowX: 'auto',
    scrollSnapType: 'x mandatory',
    WebkitOverflowScrolling: 'touch',
  },

  // ✅ Desktop modal: 3-up grid (no scroll)
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: 10,
  },

  card: {
    position: 'relative',
    borderRadius: 12,
    overflow: 'hidden',
    background: '#F3F4F6',
  },

  cardCarousel: {
    width: 220,
    height: 150,
    flexShrink: 0,
    scrollSnapAlign: 'center',
    marginRight: 8,
  },

  cardGrid: {
    width: '100%',
    height: 170,
    marginRight: 0,
  },

  img: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  },

  empty: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  uploadBadge: {
    position: 'absolute',
    left: '50%',
    bottom: 10,
    transform: 'translateX(-50%)',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 8px',
    borderRadius: 8,
    background: 'rgba(28,28,30,0.9)',
    color: '#fff',
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: '0.02em',
    lineHeight: 1,
    pointerEvents: 'none',
  },

  // a tiny tweak so the badge feels better on bigger cards
  uploadBadgeGrid: {
    bottom: 12,
    padding: '7px 10px',
    fontSize: 10.5,
  },

  plusBig: {
    fontSize: 14,
    lineHeight: 1,
    fontWeight: 600,
    marginRight: -2,
    marginBottom: 2,
  },

  dots: {
    display: 'flex',
    justifyContent: 'center',
    gap: 6,
    marginTop: 6,
  },

  dot: {
    width: 6,
    height: 6,
    borderRadius: 9999,
    background: '#E5E7EB',
  },

  dotActive: {
    background: '#0B0B0B',
  },
};
